/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable react/jsx-props-no-spreading */
import React, { FC } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import LaddaButton from "../Button/LaddaButton";
import { FormModalProps } from "../../types/components/modal";

export const FormModal: FC<FormModalProps> = (props: FormModalProps) => {
  const { t } = useTranslation();
  const {
    header,
    headerElement,
    body,
    children,
    confirmBtnName,
    cancelBtnName,
    downloadBtnName,
    loading,
    confirmBtnVariant,
    cancelBtnVariant,
    downloadBtnVariant,
    disabled,
    closeButton,
    onClose,
    confirm,
    cancel,
    download,
    isShowDownloadBtn = false,
    isShowConfirmBtn = true,
    isShowCancelBtn = true,
    centered = true,
    footerElement,
    footerClass,
    modalClass = "",
    className = "",
    fullSize,
    topLevel = false,
    container = document.body, // viewport of modal display depend on DOM container. for specific case open on another block (full screen , static or fixed DOM)
    disabledCancel,
  } = props;
  const wrapper = React.createRef<HTMLDivElement>();
  const footerClassValue = footerClass ? footerClass : "";
  return (
    <Modal
      container={container}
      dialogClassName={`${fullSize ? "full-modal-dialog" : ""} ${
        topLevel ? "z-top-level" : ""
      }`}
      {...props}
      className={`${className ? className : ""} simple-modal ${
        fullSize ? "full-modal-dialog" : ""
      }`}
      onHide={onClose}
      centered={centered}
    >
      <Modal.Header
        {...header?.props}
        onHide={onClose}
        className={` ${fullSize ? "full-modal-dialog" : ""}`}
        closeButton={closeButton}
      >
        {header && (
          <Modal.Title className="w-100" {...header?.titleProps}>
            {header?.iconClass && (
              <i
                className={header?.iconClass}
                style={{ padding: "4px 8px", fontSize: "50px" }}
              />
            )}
            <div className="header-wrapper flexbox flex-center-between">
              <span>{header?.text}</span>
            </div>
          </Modal.Title>
        )}
        {headerElement}
      </Modal.Header>
      <Modal.Body className={`${modalClass} ${footerElement ? "pb-0" : ""}`}>
        {body && (
          <p
            className="mb-3 font-size-lg text-center"
            style={{ fontSize: "0.875rem" }}
          >
            {body}
          </p>
        )}
        <div className="wrapper" ref={wrapper}>
          {children}
        </div>
      </Modal.Body>

      <Modal.Footer
        className={`pb-3 ${footerElement ? "footer-inline" : footerClassValue}`}
      >
        {footerElement && (
          <div className="footer-line flex-1">{footerElement}</div>
        )}
        {isShowDownloadBtn && download && (
          <button
            type="button"
            onClick={() => download()}
            className={`btn ${
              downloadBtnVariant ? `btn-${downloadBtnVariant}` : "btn-primary"
            } ml-2`}
          >
            {downloadBtnName || t("download")}
          </button>
        )}
        {cancel && isShowCancelBtn && (
          <Button
            variant={cancelBtnVariant || "light"}
            onClick={cancel}
            disabled={disabledCancel}
            // eslint-disable-next-line react/no-array-index-key
            key={`btn-${uuid()}`}
            className={`ladda-button btn btn-primary btn-ladda btn-ladda-spinner btn-${
              cancelBtnVariant ? cancelBtnVariant : "light"
            } ml-2`}
          >
            {cancelBtnName || t("cancel")}
          </Button>
        )}
        {confirm && isShowConfirmBtn && (
          <LaddaButton
            // eslint-disable-next-line react/no-array-index-key
            key={`btn-${uuid()}`}
            type="button"
            loading={loading}
            disabled={disabled}
            className={`btn ${
              confirmBtnVariant ? `btn-${confirmBtnVariant}` : "primary"
            } ml-2`}
            onClickHandler={confirm}
            btnName={confirmBtnName || t("general.submit")}
          />
        )}
      </Modal.Footer>
    </Modal>
  );
};
